import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Control, FieldPath, PathValue, RegisterOptions, UseFormWatch,
} from 'react-hook-form';
// eslint-disable-next-line import/no-unresolved
import { UseFormGetFieldState, UseFormResetField, UseFormSetValue } from 'react-hook-form/dist/types/form';

import { ChangeHandlerType, GeneratorChangeHandlerType } from 'common/components/FormHF/types';
import { FieldsType, ValidationTypes } from 'common/types/fields';

import {
  isMaxLengthValidationType,
  isMaxValidationType,
  isMinLengthValidationType,
  isMinValidationType,
} from 'utils/isMinMaxValidation';
import { getValidationErrorMessage } from 'utils/getValidationErrorMessage';
import { generateFieldNameByGroup } from 'common/components/FormHF/utils';

import InputHF from 'common/components/FormHF/InputHF';
import TextareaHF from 'common/components/FormHF/TextareaHF';
import SelectHF from 'common/components/FormHF/SelectHF';
import CheckboxesList from 'common/components/FormHF/CheckboxesList';
import RadioButtonsList from 'common/components/FormHF/RadioButtonsList';
import FieldWrapper from 'common/components/FieldWrapper';
import { useDebounceCallback } from 'hooks/useDebouncedCallback';
import { FallbackFormValues } from 'utils/type-tools';
import { GeneratorFieldType, ICustomDescriptionProps, ICustomOptionProps } from './types';

interface FieldGeneratorProps<TFormValue extends FallbackFormValues> {
  name: FieldPath<TFormValue>,
  placeholder?: string,
  control: Control<TFormValue>,
  field: GeneratorFieldType,
  watch: UseFormWatch<Record<keyof TFormValue, any>>,
  setValue: UseFormSetValue<TFormValue>,
  getFieldState: UseFormGetFieldState<TFormValue>,
  onChangeHandler?: GeneratorChangeHandlerType<TFormValue>,
  isDisabled: boolean,
  isIgnoredForScrolling?: boolean,
  resetField: UseFormResetField<TFormValue>
  renderOptionLabelComponent?: (fieldId: keyof TFormValue) => React.FC<ICustomOptionProps> | undefined,
  renderDescriptionComponent?: (fieldId: keyof TFormValue) => React.FC<ICustomDescriptionProps> | undefined,
  Component?: {
    RadioButtonsList?: typeof RadioButtonsList,
  },
  debounceDelay?: number,
  className?: string,
}

const FieldGenerator = <TFormValue extends FallbackFormValues = FallbackFormValues>({
  name,
  control,
  field,
  watch,
  setValue,
  isDisabled,
  resetField,
  getFieldState,
  isIgnoredForScrolling,
  onChangeHandler,
  renderOptionLabelComponent,
  renderDescriptionComponent,
  debounceDelay,
  Component: Overrides,
  className,
  placeholder,
}: FieldGeneratorProps<TFormValue>) => {
  const isRangeInput: boolean = useMemo<boolean>(() => !!field.validation?.find((validation) => validation.type === ValidationTypes.range), [field.validation]);

  const rulesObject = useMemo<RegisterOptions>(() => {
    const rulesTemplate: RegisterOptions = {};
    field.validation?.forEach((v) => {
      if (v.type === ValidationTypes.required) {
        rulesTemplate.required = {
          value: true,
          message: getValidationErrorMessage(v),
        };
      } else if (v.type === ValidationTypes.range) {
        if (isMaxValidationType(v)) {
          if (
            !rulesTemplate.max
              || (typeof rulesTemplate.max === 'object' && rulesTemplate.max.value > v.maxValue)
              || (typeof rulesTemplate.max !== 'object' && rulesTemplate.max > v.maxValue)
          ) {
            rulesTemplate.max = {
              value: v.maxValue,
              message: getValidationErrorMessage(v),
            };
          }
        }
        if (isMinValidationType(v)) {
          if (
            !rulesTemplate.min
              || (typeof rulesTemplate.min === 'object' && rulesTemplate.min.value < v.minValue)
              || (typeof rulesTemplate.min !== 'object' && rulesTemplate.min < v.minValue)
          ) {
            rulesTemplate.min = {
              value: v.minValue,
              message: getValidationErrorMessage(v),
            };
          }
        }
      } else if (v.type === ValidationTypes.length && isMaxLengthValidationType(v)) {
        rulesTemplate.maxLength = {
          value: v.maxValue,
          message: getValidationErrorMessage(v),
        };
      } else if (v.type === ValidationTypes.length && isMinLengthValidationType(v)) {
        rulesTemplate.minLength = {
          value: v.minValue,
          message: getValidationErrorMessage(v),
        };
      } else if (v.type === ValidationTypes.pattern) {
        rulesTemplate.pattern = {
          value: new RegExp(v.pattern),
          message: getValidationErrorMessage(v),
        };
      }
    });
    return rulesTemplate;
  }, [field]);

  const onChangeHandlerDebounced = useDebounceCallback(onChangeHandler, debounceDelay || 500);

  const onChangeHandlerWrapper = useCallback<ChangeHandlerType<TFormValue>>((fieldVal) => {
    if (onChangeHandler) {
      switch (field.type) {
        case FieldsType.textarea:
        case FieldsType.input: {
          onChangeHandlerDebounced(field.type, name, fieldVal);
          break;
        }
        default: {
          onChangeHandler(field.type, name, fieldVal);
        }
      }
    }
  }, [field.type, name, onChangeHandler, onChangeHandlerDebounced]);

  const RadioButtonsListCmp = Overrides?.RadioButtonsList || RadioButtonsList;

  const renderOptionLabelComponentWrapper = useCallback(
    () => renderOptionLabelComponent && renderOptionLabelComponent(field.name as keyof TFormValue),
    [field.name, renderOptionLabelComponent],
  );

  const Description = useMemo(() => {
    const Component = renderDescriptionComponent && renderDescriptionComponent(field.name as keyof TFormValue);
    return Component || (() => null);
  }, [renderDescriptionComponent]);

  useEffect(() => {
    switch (field.type) {
      case FieldsType.textarea:
      case FieldsType.input: {
        field.value?.selected
          ? setValue(field.name, field.value?.selected?.value as PathValue<TFormValue, any>)
          : resetField(field.name as FieldPath<TFormValue>, { defaultValue: null });
        break;
      }
      case FieldsType.checkboxes: {
        field.value?.selected?.forEach((selectedItem) => {
          const itemName = generateFieldNameByGroup(name, selectedItem.id);
          setValue(itemName as FieldPath<TFormValue>, selectedItem.id as PathValue<TFormValue, any>);
        });
        if (!field.value?.selected) {
          field.value?.options?.forEach((selectedItem) => {
            const itemName = generateFieldNameByGroup(name, selectedItem.id);
            resetField(itemName as FieldPath<TFormValue>, { defaultValue: null });
          });
        }
        break;
      }
      case FieldsType.radioGroup: {
        field.value?.selected && field.value?.selected[0]
          ? setValue(field.name, field.value.selected[0].id as PathValue<TFormValue, any>)
          : resetField(field.name as FieldPath<TFormValue>, { defaultValue: null });
        break;
      }
      case FieldsType.dropdown: {
        field.value?.selected
          ? setValue(field.name, field.value.selected[0] as PathValue<TFormValue, any>) // supported only single value
          : resetField(field.name as FieldPath<TFormValue>, { defaultValue: null });
        break;
      }
      default:
        break;
    }
  }, [field, name, resetField, setValue]);

  const value = watch(name as any);
  const hasValue = !!value;

  switch (field.type) {
    case FieldsType.input: {
      return (
        <FieldWrapper
          className={className}
          name={name}
          hasValue={hasValue}
          isIgnoredForScrolling={isIgnoredForScrolling}
        >
          <InputHF
            name={name}
            label={field.title}
            control={control}
            rules={rulesObject}
            placeholder={placeholder}
            type={isRangeInput ? 'number' : 'text'}
            inputMode={isRangeInput ? 'decimal' : undefined}
            onChangeHandler={onChangeHandlerWrapper}
            disabled={isDisabled}
          />
          <Description field={field} />
        </FieldWrapper>
      );
    }
    case FieldsType.textarea: {
      return (
        <FieldWrapper
          className={className}
          name={name}
          hasValue={hasValue}
          isIgnoredForScrolling={isIgnoredForScrolling}
        >
          <TextareaHF
            name={name}
            label={field.title}
            control={control}
            rules={rulesObject}
            placeholder={placeholder}
            type={isRangeInput ? 'number' : 'text'}
            inputMode={isRangeInput ? 'decimal' : undefined}
            onChangeHandler={onChangeHandlerWrapper}
            disabled={isDisabled}
          />
          <Description field={field} />
        </FieldWrapper>
      );
    }
    case FieldsType.checkboxes: {
      if (field.value?.options) {
        return (
          <FieldWrapper
            className={className}
            name={name}
            hasValue={hasValue}
            isIgnoredForScrolling={isIgnoredForScrolling}
          >
            <CheckboxesList
              getFieldState={getFieldState}
              name={name}
              control={control}
              rules={rulesObject}
              setValue={setValue}
              watch={watch}
              optionsList={field.value?.options ?? []}
              selectedList={field.value?.selected}
              onChangeHandler={onChangeHandlerWrapper}
              disabled={isDisabled}
            />
            <Description field={field} />
          </FieldWrapper>
        );
      }
      return null;
    }
    case FieldsType.radioGroup: {
      return (
        <FieldWrapper
          className={className}
          name={name}
          hasValue={hasValue}
          isIgnoredForScrolling={isIgnoredForScrolling}
        >
          <div data-scroll-anchor style={{ position: 'absolute' }} />
          <RadioButtonsListCmp
            getFieldState={getFieldState}
            name={name}
            control={control}
            rules={rulesObject}
            setValue={setValue}
            watch={watch}
            optionsList={field.value?.options ?? []}
            selectedList={field.value?.selected}
            onChangeHandler={onChangeHandlerWrapper}
            disabled={isDisabled}
            renderOptionLabelComponent={renderOptionLabelComponentWrapper}
          />
          <Description field={field} />
        </FieldWrapper>
      );
    }
    case FieldsType.dropdown: {
      return (
        <FieldWrapper
          className={className}
          name={name}
          hasValue={hasValue}
          isIgnoredForScrolling={isIgnoredForScrolling}
        >
          <SelectHF
            name={name}
            label={field.title}
            control={control}
            options={field.value?.options ?? []}
            rules={rulesObject}
            onChangeHandler={onChangeHandlerWrapper}
            disabled={isDisabled}
            renderOptionLabelComponent={renderOptionLabelComponentWrapper}
          />
          <Description field={field} />
        </FieldWrapper>
      );
    }
    default: return null;
  }
};

// @ts-ignore
export default FieldGenerator;
